import { useMemo } from "react";

import type { Props as BaseLinkProps } from "shared/components/common/base-link";
import { section as sectionData, tracking } from "shared/data";
import { useCurrentPageType } from "shared/hooks";
import { useEditionValue } from "shared/lib/edition";
import { useGetHomeAsPath } from "shared/lib/router";

import { useScrollOverThreshold } from "scmp-app/lib/hooks/use-scroll-over-threshold";
import { page as HomePage } from "scmp-app/pages/home";
import { page as SectionPage } from "scmp-app/pages/section";
import { Variant } from "scmp-app/pages/section/enums";

export const useSwapOnScrollEffect = (threshold: number) => {
  // SCMPWEB-10011 T2-B: sentinel-based IO instead of the ahooks useScroll
  // listener (per-frame JS). Semantics: previous implementation was
  // `top >= threshold`; the sentinel flips at `scrollY > threshold` (~1px
  // later, within the ±2px parity tolerance documented in the plan).
  const shouldSwap = useScrollOverThreshold(threshold, "Header swap");
  return { shouldSwap };
};

export const useMastheadTracking = () => {
  const currentPageType = useCurrentPageType();

  return useMemo(
    () => ({
      module: tracking.module.Masthead,
      pgtype: currentPageType,
    }),
    [currentPageType],
  );
};

export const useHomepageLogoLinkProps = (): BaseLinkProps => {
  const edition = useEditionValue();
  const homeAsPath = useGetHomeAsPath();
  const mastheadQueryParameter = useMastheadTracking();

  return useMemo(() => {
    const { pathname: homePagePathname, query: homePageQuery } = HomePage.route({
      edition,
      slide: "main",
    });

    return {
      anchorProps: { "aria-label": "SCMP homepage" },
      as: homeAsPath,
      pathname: homePagePathname,
      query: { ...homePageQuery, ...mastheadQueryParameter },
    };
  }, [edition, homeAsPath, mastheadQueryParameter]);
};

export const useStyleLogoLinkProps = (): BaseLinkProps => {
  const mastheadQueryParameter = useMastheadTracking();

  return useMemo(() => {
    const { pathname: styleSectionPagePathname, query: styleSectionPageQuery } = SectionPage.route({
      entityId: sectionData.style.entityId,
      sectionVariant: Variant.Style,
    });

    return {
      anchorProps: { "aria-label": "SCMP style section" },
      as: sectionData.style.urlAlias,
      pathname: styleSectionPagePathname,
      query: { ...styleSectionPageQuery, ...mastheadQueryParameter },
    };
  }, [mastheadQueryParameter]);
};
